import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Button,
  Drawer,
  Group,
  Select,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';

import { useEditContentMaterial } from '../api';
import { getEditableStatusOptions } from '../constants/status';
import { useContentCategoryOptions } from '../hooks/useContentCategoryOptions';
import { contentTypes } from '../types';

type EditTextDrawerProps = {
  itemId: string;
  pageId?: string;
  managerId?: string;
  initialValues: {
    name: string;
    category: string;
    status: contentTypes.Status;
    textContent: string;
  };
};

type Fields = EditTextDrawerProps['initialValues'];

export const EditTextDrawer = NiceModal.create(
  ({ itemId, pageId, managerId, initialValues }: EditTextDrawerProps) => {
    const modal = useModal();
    const editMaterial = useEditContentMaterial({ pageId });
    const categoryOptions = useContentCategoryOptions(pageId);

    const form = useForm<Fields>({
      initialValues,
      validate: {
        name: (value) => (!value.trim() ? 'Обязательное поле' : null),
        textContent: (value) => (!value.trim() ? 'Обязательное поле' : null),
      },
    });

    const save = (values: Fields, status: contentTypes.Status) => {
      editMaterial.mutate(
        {
          id: itemId,
          managerId,
          name: values.name.trim(),
          category: values.category.trim(),
          status,
          textContent: values.textContent.trim(),
        },
        { onSuccess: () => modal.remove() }
      );
    };

    return (
      <Drawer
        opened={modal.visible}
        onClose={() => modal.remove()}
        position="right"
        size={420}
        title="Редактировать материал"
        overlayProps={{ opacity: 0.4, blur: 1.5 }}
        padding="md"
      >
        <Text size="sm" c="dimmed" mb="md">
          Тип: text
        </Text>

        <form onSubmit={form.onSubmit((values) => save(values, values.status))}>
          <Stack gap="md">
            <TextInput label="Название" withAsterisk {...form.getInputProps('name')} />
            <Select
              label="Категория"
              value={form.values.category}
              onChange={(value) => form.setFieldValue('category', value || '')}
              data={categoryOptions}
              searchable
              clearable
              nothingFoundMessage="Нет категории"
            />
            <SegmentedControl
              data={getEditableStatusOptions(initialValues.status)}
              value={form.values.status}
              onChange={(value) => form.setFieldValue('status', value as contentTypes.Status)}
              fullWidth
            />
            <Textarea
              label="Текст"
              withAsterisk
              minRows={10}
              autosize
              {...form.getInputProps('textContent')}
            />

            <Group justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => modal.remove()}>
                Отмена
              </Button>
              <Button type="submit" loading={editMaterial.isLoading}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Drawer>
    );
  }
);
